import React, { ChangeEvent, CSSProperties, useCallback, useEffect, useState } from "react";
import ReactModal from "react-modal";
import { V2 } from "../Api";

import "../modal.css";
import Style from "./TextTemplateDialog.module.css";

interface ITextTemplateDialogProps {
    isOpen: boolean;
    templateGroup: string;
    onRequestClose: () => void;
    onTemplateFinished: (result: string) => void;
    onRequestResolveLink?: (link: string) => PromiseLike<string>;
}

const EMPTY_TEMPLATE: ITextTemplate = {
    content: []
};

const TextTemplateDialog: React.FC<ITextTemplateDialogProps> = function (props) {
    const [values, setValues] = useState({});
    const [templateId, setTemplateId] = useState("");
    const [template, setTemplate] = useState<ITextTemplate>(EMPTY_TEMPLATE);
    const [templates, setTemplates] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const onChange = useCallback((e: TextPlaceholderChangeEvent) => {
        const { name, value } = e.currentTarget;

        const newValues = Object.assign({}, values, { [name]: value });
        setValues(newValues);
    }, [values]);

    useEffect(() => {
        setValues({});
        setTemplate(EMPTY_TEMPLATE);
        setTemplateId("");
        setIsLoading(false);
    }, [props.isOpen]);

    useEffect(() => {
        if (props.templateGroup) {
            V2.get<any>(`document/templates?group=${props.templateGroup}`).then(result => {
                setTemplates((result.templates as any[]).sort((a,b) => a.name.localeCompare(b.name)));
            });
        }
    }, [props.templateGroup]);

    useEffect(() => {
        const requestResolveLink = props.onRequestResolveLink || (async () => "");

        if (!templateId) {
            setTemplate(EMPTY_TEMPLATE);
            return;
        }

        (async () => {
            // Load template
            const values: { [name: string]: any } = {};

            try {
                setIsLoading(true);

                const result = await V2.get<any>(`document/templates/${templateId}`);
                const temp = JSON.parse(result.json);

                // Resolve linked placeholders
                for (const item of temp.content) {
                    if (typeof item === "object" && item.link) {
                        const value = await requestResolveLink(item.link);
                        values[item.id] = value;
                    }
                }

                setTemplate(temp);
                setValues(values);
                setIsLoading(false);
            }
            catch (e) {
                setTemplate({
                    content: [e.message || "Failed to load template!"]
                });
                setIsLoading(false);
            }
        })();
    }, [templateId, props.onRequestResolveLink]);

    return <ReactModal isOpen={props.isOpen} onRequestClose={props.onRequestClose}
        overlayClassName="modal-overlay" className="modal-content modal-content-700" portalClassName="modal-portal"
        bodyOpenClassName="modal-open"
        contentLabel="Select text template"
    >
        <div className="info-block input-form">
            <header>
                <h3>Template selection</h3>
            </header>

            <section>
                <select value={templateId} onChange={(e) => setTemplateId(e.currentTarget.value)}>
                    <option value="">Select a template</option>
                    {templates.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
            </section>
            <div className={Style.editor}>
                {isLoading
                    ? <div className="spinner spinner-center lds-dual-ring" />
                    : renderTemplate(template, values, onChange)}
            </div>
            <small>
                Select the underlined blanks to fill in parts of the template
            </small>
            &nbsp;
            <div className="action-panel-dialog">
                <span className="spring" />
                <button type="button" className="button green" onClick={() => { props.onTemplateFinished?.(processTemplate(template, values)); props.onRequestClose(); }}>Use Template</button>
                <button type="button" className="button" onClick={() => props.onRequestClose()}>Cancel</button>
            </div>
        </div>
    </ReactModal>;
}

type TemplateElement =
    string | {
        type: string,
        id: string,
        width?: number,
        suggestions?: string[],
        link?: string
    };

interface ITextTemplate {
    content: TemplateElement[];
}

function renderTemplate(template: ITextTemplate, values: any, onChange: (e: TextPlaceholderChangeEvent) => void) {
    const output = [];

    for (let i = 0; i < template.content.length; i++) {
        const item = template.content[i];
        if (typeof item === "object") {
            switch (item.type) {
                case "text":
                    if (!item.id) {
                        throw new Error("Text template placeholder has no id!");
                    }

                    const placeholder = <TextPlaceholder
                        key={item.id}
                        className={Style.placeholder}
                        width={item.width || 5}
                        id={item.id}
                        suggestions={item.suggestions}
                        value={values[item.id] || ""}
                        onChange={onChange}
                    />;

                    output.push(placeholder);
                    break;
            }
        }
        else if (typeof item === "string") {
            if (item.includes("\n")) {
                // Convert newlines to HTML breaks
                const stringParts = item.split("\n");
                const stringElements = [];
                for (let j = 0; j < stringParts.length; j++) {
                    if (j > 0) {
                        stringElements.push(<br key={`${i}.${j}.br`} />);
                    }
                    stringElements.push(<span key={`${i}.${j}`}>{stringParts[j]}</span>);
                }

                Array.prototype.push.apply(output, stringElements);
            }
            else {
                output.push(<span key={i}>{item}</span>);
            }
        }
    }

    return output;
}

function processTemplate(template: ITextTemplate, values: { [id: string]: any }) {
    const output = [];

    for (const item of template.content) {
        switch (typeof item) {
            case "string":
                output.push(item);
                break;
            case "object":
                output.push(values[item.id] || "");
                break;
        }
    }

    return output.join("");
}

type TextPlaceholderChangeEvent = ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>;

interface ITextPlaceholderProps {
    value: string;
    onChange: (e: TextPlaceholderChangeEvent) => void;
    id: string;
    suggestions?: string[];
    className?: string;
    style?: CSSProperties;
    width?: number;
}

const TextPlaceholder: React.FC<ITextPlaceholderProps> = function (props) {
    const [isActive, setIsActive] = useState(false);
    const style: CSSProperties = { width: props.width ?`${props.width * 16}px` : undefined };

    if (isActive) {
        return <>
            {props.suggestions
                ? <datalist id={`${props.id}_list`}>
                    {props.suggestions.map((a: string) => <option key={a}>{a}</option>)}
                </datalist>
                : null}
            <input
                className={Style.placeholder}
                style={style}
                list={props.suggestions ? `${props.id}_list` : undefined}
                onBlur={() => setIsActive(false)}
                name={props.id}
                onChange={props.onChange}
                value={props.value}
                autoFocus={true}
                autoComplete="off" />
        </>;
    }
    else {
        return <span
            className={Style.placeholder}
            style={!props.value ? style : undefined}
            tabIndex={0}
            onFocus={() => setIsActive(true)}>{props.value}</span>;
    }
}

export default TextTemplateDialog;
